/**
 * woNotes.js — the notes a work order shows (IDI-551).
 *
 * Work-order notes show on their work order only. Besides the attributed list
 * ({id, content, author, author_id, created_at}), two older shapes exist:
 *   - a STRING `notes` value (the old create sheet, legacy records): shown as
 *     one read-only entry by "Earlier note (author not recorded)";
 *   - a migrated `instructions` value (BIDIT 1.0): shown as one read-only
 *     entry labelled "Instructions (from BIDIT 1.0)".
 * Both survive later adds: `instructions` is never written by the notes path,
 * and a string `notes` value is kept as an unattributed entry in the list the
 * first time a note is added (the field cannot be both a string and a list).
 */
import { LEGACY_AUTHOR_LABEL } from './displayName';

export const INSTRUCTIONS_LABEL = 'Instructions (from BIDIT 1.0)';

function woCreatedAt(wo) {
  return wo?.createdAt || wo?.created_at || '';
}

/** Every note entry the work order shows, stored order then the legacy ones. */
export function woNotes(wo) {
  if (!wo) return [];
  const out = [];
  const raw = wo.notes;
  if (Array.isArray(raw)) {
    raw.forEach((n) => { if (n && typeof n === 'object') out.push(n); });
  } else if (typeof raw === 'string' && raw.trim()) {
    out.push({
      id: `legacy-${wo.id || 'wo'}`,
      content: raw,
      author: LEGACY_AUTHOR_LABEL,
      author_id: null,
      created_at: woCreatedAt(wo),
      readOnly: true,
      derived: 'string',
    });
  }
  const instr = typeof wo.instructions === 'string' ? wo.instructions.trim() : '';
  if (instr) {
    out.push({
      id: `instructions-${wo.id || 'wo'}`,
      content: wo.instructions,
      author: INSTRUCTIONS_LABEL,
      author_id: null,
      created_at: woCreatedAt(wo),
      readOnly: true,
      derived: 'instructions',
    });
  }
  return out;
}

/**
 * The `notes` list to persist after adding `note`: the new note, then the
 * stored notes (a legacy string kept as its unattributed entry). The derived
 * `instructions` entry is never written; its source field stays as it is.
 */
export function woNotesForSave(note, shown) {
  const kept = (Array.isArray(shown) ? shown : [])
    .filter((n) => n && n.derived !== 'instructions')
    .map(({ readOnly, derived, ...rest }) => rest);
  return [note, ...kept];
}

export default woNotes;
